import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  HStack,
  Text,
  useToast,
  Switch,
  Select,
  Divider,
  Avatar,
  Badge,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tooltip,
  Spinner,
  Stack,
  Flex,
  Spacer,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiRotateCcw } from 'react-icons/fi';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

function GenerateCardsSection() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setResult(null);
      const data = await apiService.makeRequest('/members/generate-missing-cards', { method: 'POST' });
      setResult(data);
      toast({
        title: data.updated > 0 ? 'Numéros générés' : 'Aucun changement',
        description: data.message,
        status: data.updated > 0 ? 'success' : 'info',
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontWeight="medium" mb={1}>Numéros de carte manquants</Text>
        <Text fontSize="sm" color="gray.600" mb={4}>
          Génère automatiquement un numéro de carte unique (format <code>CARD-000001</code>) pour tous les membres qui n'en ont pas encore.
        </Text>
        <Button colorScheme="green" onClick={handleGenerate} isLoading={loading} leftIcon={<span>🎫</span>}>
          Générer les numéros manquants
        </Button>
      </Box>
      {result && (
        <Box bg={result.updated > 0 ? 'green.50' : 'gray.50'} p={4} borderRadius="md" border="1px solid" borderColor={result.updated > 0 ? 'green.200' : 'gray.200'}>
          <Text fontSize="sm" color={result.updated > 0 ? 'green.700' : 'gray.600'}>{result.message}</Text>
        </Box>
      )}
    </VStack>
  );
}

const ACTION_LABELS = { CREATE: 'Création', UPDATE: 'Modification', DELETE: 'Suppression' };
const ACTION_COLORS = { CREATE: 'green', UPDATE: 'blue', DELETE: 'red' };
const ENTITY_LABELS = { contribution: 'Cotisation', member: 'Membre', enrollment: 'Adhésion', expense: 'Dépense' };

function AuditLogSection() {
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ user_id: '', entity_type: '', action: '', date_from: '', date_to: '' });
  const [revertingId, setRevertingId] = useState(null);
  const limit = 20;

  const fetchLogs = async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const data = await apiService.makeRequest(`/audit?${params}`);
      setLogs(data.data.logs);
      setTotal(data.data.total);
      setPage(p);
    } catch (e) {
      toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await apiService.makeRequest('/audit/users');
      setUsers(data.data);
    } catch {}
  };

  useEffect(() => { fetchLogs(1); fetchUsers(); }, []); // eslint-disable-line

  const handleRevert = async (log) => {
    if (!window.confirm(`Restaurer la version précédente de cette ${ENTITY_LABELS[log.entity_type] || log.entity_type} ?`)) return;
    setRevertingId(log.id);
    try {
      await apiService.makeRequest(`/audit/revert/${log.id}`, { method: 'POST' });
      toast({ title: 'Restauré', description: 'La version précédente a été restaurée.', status: 'success', duration: 4000 });
      fetchLogs(page);
    } catch (e) {
      toast({ title: 'Erreur', description: e.message, status: 'error', duration: 4000 });
    } finally {
      setRevertingId(null);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <VStack align="stretch" spacing={4}>
      {/* Filtres */}
      <Stack direction={{ base: 'column', md: 'row' }} spacing={3} flexWrap="wrap">
        <Select placeholder="Utilisateur" value={filters.user_id} onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))} maxW="200px">
          {users.map(u => <option key={u.user_id} value={u.user_id}>{u.user_label}</option>)}
        </Select>
        <Select placeholder="Type" value={filters.entity_type} onChange={e => setFilters(f => ({ ...f, entity_type: e.target.value }))} maxW="160px">
          {Object.entries(ENTITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
        <Select placeholder="Action" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))} maxW="160px">
          {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
        <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} maxW="160px" placeholder="Du" />
        <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} maxW="160px" placeholder="Au" />
        <Button colorScheme="blue" onClick={() => fetchLogs(1)}>Filtrer</Button>
        <Button variant="ghost" onClick={() => { setFilters({ user_id: '', entity_type: '', action: '', date_from: '', date_to: '' }); setTimeout(() => fetchLogs(1), 0); }}>Réinitialiser</Button>
      </Stack>

      {loading ? (
        <Flex justify="center" py={8}><Spinner size="lg" /></Flex>
      ) : (
        <>
          <Text fontSize="sm" color="gray.500">{total} entrée{total > 1 ? 's' : ''}</Text>
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Utilisateur</Th>
                  <Th>Action</Th>
                  <Th>Objet</Th>
                  <Th>ID</Th>
                  <Th>Détails</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {logs.length === 0 && (
                  <Tr><Td colSpan={7} textAlign="center" color="gray.400">Aucune entrée</Td></Tr>
                )}
                {logs.map(log => {
                  const old = log.old_data ? (typeof log.old_data === 'string' ? JSON.parse(log.old_data) : log.old_data) : null;
                  const nw = log.new_data ? (typeof log.new_data === 'string' ? JSON.parse(log.new_data) : log.new_data) : null;
                  const canRevert = log.action === 'UPDATE' && old;
                  return (
                    <Tr key={log.id} _hover={{ bg: 'gray.50' }}>
                      <Td whiteSpace="nowrap" fontSize="xs">{log.created_at}</Td>
                      <Td fontSize="sm">{log.user_label}</Td>
                      <Td>
                        <Badge colorScheme={ACTION_COLORS[log.action] || 'gray'}>
                          {ACTION_LABELS[log.action] || log.action}
                        </Badge>
                      </Td>
                      <Td fontSize="sm">{ENTITY_LABELS[log.entity_type] || log.entity_type}</Td>
                      <Td fontSize="xs" color="gray.500">#{log.entity_id}</Td>
                      <Td fontSize="xs" maxW="260px">
                        {old && nw && (
                          <VStack align="start" spacing={0}>
                            {Object.keys(nw).filter(k => String(old[k]) !== String(nw[k])).map(k => (
                              <Text key={k}>
                                <Text as="span" color="gray.500">{k}: </Text>
                                <Text as="span" textDecoration="line-through" color="red.400">{String(old[k] ?? '')}</Text>
                                {' → '}
                                <Text as="span" color="green.600">{String(nw[k] ?? '')}</Text>
                              </Text>
                            ))}
                          </VStack>
                        )}
                      </Td>
                      <Td>
                        {canRevert && (
                          <Tooltip label="Restaurer la version précédente">
                            <IconButton
                              size="xs"
                              icon={<FiRotateCcw />}
                              variant="ghost"
                              colorScheme="orange"
                              isLoading={revertingId === log.id}
                              onClick={() => handleRevert(log)}
                              aria-label="Restaurer"
                            />
                          </Tooltip>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {totalPages > 1 && (
            <Flex justify="center" gap={2}>
              <Button size="sm" onClick={() => fetchLogs(page - 1)} isDisabled={page <= 1}>Précédent</Button>
              <Text alignSelf="center" fontSize="sm">Page {page} / {totalPages}</Text>
              <Button size="sm" onClick={() => fetchLogs(page + 1)} isDisabled={page >= totalPages}>Suivant</Button>
            </Flex>
          )}
        </>
      )}
    </VStack>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const toast = useToast();
  
  // Modals pour édition
  const { isOpen: isGroupOpen, onOpen: onGroupOpen, onClose: onGroupClose } = useDisclosure();
  const { isOpen: isFunctionOpen, onOpen: onFunctionOpen, onClose: onFunctionClose } = useDisclosure();
  const { isOpen: isTypeOpen, onOpen: onTypeOpen, onClose: onTypeClose } = useDisclosure();
  
  // Alert dialog pour suppression
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  // État pour le profil
  const [profileData, setProfileData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse_postale: ''
  });

  // État pour le mot de passe
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // État pour les groupes
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState({ nom: '', description: '' });
  const [editingGroup, setEditingGroup] = useState(null);

  // État pour les fonctions
  const [functions, setFunctions] = useState([]);
  const [newFunction, setNewFunction] = useState({ nom: '', description: '' });
  const [editingFunction, setEditingFunction] = useState(null);

  // État pour les types de contributions
  const [contributionTypes, setContributionTypes] = useState([]);
  const [newContributionType, setNewContributionType] = useState({
    nom: '',
    description: '',
    montant_suggere: '',
    recurrence: 'mensuelle'
  });
  const [editingType, setEditingType] = useState(null);

  // État pour la suppression
  const [deleteItem, setDeleteItem] = useState(null);

  useEffect(() => {
    fetchUserProfile();
    if (user?.is_admin) {
      fetchGroups();
      fetchFunctions();
      fetchContributionTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchUserProfile = async () => {
    try {
      const data = await apiService.getCurrentUser();
      setProfileData({
        nom: data.nom || '',
        prenom: data.prenom || '',
        email: data.email || '',
        telephone: data.telephone || '',
        adresse_postale: data.adresse_postale || ''
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      console.log('🔍 Fetching groups...');
      const data = await apiService.makeRequest('/groups');
      console.log('📊 Groups data received:', data);
      
      // La réponse est maintenant directement un tableau
      if (Array.isArray(data)) {
        setGroups(data);
        console.log('✅ Groups set:', data.length);
      } else if (data.groups && Array.isArray(data.groups)) {
        setGroups(data.groups);
        console.log('✅ Groups set from data.groups:', data.groups.length);
      } else {
        console.warn('⚠️ Unexpected groups data format:', data);
        setGroups([]);
      }
    } catch (err) {
      console.error('❌ Error fetching groups:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les groupes',
        status: 'error',
        duration: 3000,
      });
      setGroups([]);
    }
  };

  const fetchFunctions = async () => {
    try {
      console.log('🔍 Fetching functions...');
      const data = await apiService.makeRequest('/functions');
      console.log('📊 Functions data received:', data);
      
      // La réponse est maintenant directement un tableau
      if (Array.isArray(data)) {
        setFunctions(data);
        console.log('✅ Functions set:', data.length);
      } else if (data.functions && Array.isArray(data.functions)) {
        setFunctions(data.functions);
        console.log('✅ Functions set from data.functions:', data.functions.length);
      } else {
        console.warn('⚠️ Unexpected functions data format:', data);
        setFunctions([]);
      }
    } catch (err) {
      console.error('❌ Error fetching functions:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les fonctions',
        status: 'error',
        duration: 3000,
      });
      setFunctions([]);
    }
  };

  const fetchContributionTypes = async () => {
    try {
      console.log('🔍 Fetching contribution types...');
      const data = await apiService.makeRequest('/contributions/types/list');
      console.log('📊 Contribution types data received:', data);
      
      if (Array.isArray(data)) {
        setContributionTypes(data);
        console.log('✅ Contribution types set:', data.length);
      } else {
        console.warn('⚠️ Unexpected contribution types data format:', data);
        setContributionTypes([]);
      }
    } catch (err) {
      console.error('❌ Error fetching contribution types:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les types de contributions',
        status: 'error',
        duration: 3000,
      });
      setContributionTypes([]);
    }
  };


  const handleProfileUpdate = async () => {
    try {
      await apiService.makeRequest(`/members/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify(profileData)
      });
      toast({
        title: 'Profil mis à jour',
        status: 'success',
        duration: 3000
      });
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        status: 'error',
        duration: 5000
      });
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: 'Erreur',
        description: 'Les mots de passe ne correspondent pas',
        status: 'error',
        duration: 5000
      });
      return;
    }

    try {
      await apiService.makeRequest('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });
      toast({
        title: 'Mot de passe modifié',
        status: 'success',
        duration: 3000
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        status: 'error',
        duration: 5000
      });
    }
  };

  // ==================== GROUPES ====================
  const openGroupModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setNewGroup({ nom: group.nom, description: group.description || '' });
    } else {
      setEditingGroup(null);
      setNewGroup({ nom: '', description: '' });
    }
    onGroupOpen();
  };

  const handleSaveGroup = async () => {
    try {
      const endpoint = editingGroup ? `/groups/${editingGroup.id}` : '/groups';
      const method = editingGroup ? 'PUT' : 'POST';

      console.log('💾 Saving group:', { endpoint, method, data: newGroup });

      await apiService.makeRequest(endpoint, {
        method,
        body: JSON.stringify(newGroup)
      });

      toast({
        title: editingGroup ? 'Groupe mis à jour' : 'Groupe ajouté',
        status: 'success',
        duration: 3000
      });
      
      setNewGroup({ nom: '', description: '' });
      setEditingGroup(null);
      onGroupClose();
      fetchGroups();
    } catch (err) {
      console.error('❌ Error saving group:', err);
      toast({
        title: 'Erreur',
        description: err.message || 'Erreur lors de la sauvegarde du groupe',
        status: 'error',
        duration: 5000
      });
    }
  };


  const handleDeleteGroup = async (id) => {
    try {
      await apiService.makeRequest(`/groups/${id}`, {
        method: 'DELETE'
      });
      toast({
        title: 'Groupe supprimé',
        status: 'success',
        duration: 3000
      });
      fetchGroups();
      onDeleteClose();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        status: 'error',
        duration: 5000
      });
    }
  };

  // ==================== FONCTIONS ====================
  const openFunctionModal = (func = null) => {
    if (func) {
      setEditingFunction(func);
      setNewFunction({ nom: func.nom, description: func.description || '' });
    } else {
      setEditingFunction(null);
      setNewFunction({ nom: '', description: '' });
    }
    onFunctionOpen();
  };

  const handleSaveFunction = async () => {
    try {
      const endpoint = editingFunction ? `/functions/${editingFunction.id}` : '/functions';
      const method = editingFunction ? 'PUT' : 'POST';

      console.log('💾 Saving function:', { endpoint, method, data: newFunction });

      await apiService.makeRequest(endpoint, {
        method,
        body: JSON.stringify(newFunction)
      });

      toast({
        title: editingFunction ? 'Fonction mise à jour' : 'Fonction ajoutée',
        status: 'success',
        duration: 3000
      });
      
      setNewFunction({ nom: '', description: '' });
      setEditingFunction(null);
      onFunctionClose();
      fetchFunctions();
    } catch (err) {
      console.error('❌ Error saving function:', err);
      toast({
        title: 'Erreur',
        description: err.message || 'Erreur lors de la sauvegarde de la fonction',
        status: 'error',
        duration: 5000
      });
    }
  };

  const handleDeleteFunction = async (id) => {
    try {
      await apiService.makeRequest(`/functions/${id}`, {
        method: 'DELETE'
      });
      toast({
        title: 'Fonction supprimée',
        status: 'success',
        duration: 3000
      });
      fetchFunctions();
      onDeleteClose();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        status: 'error',
        duration: 5000
      });
    }
  };

  // ==================== TYPES DE CONTRIBUTIONS ====================
  const openTypeModal = (type = null) => {
    if (type) {
      setEditingType(type);
      setNewContributionType({
        nom: type.nom,
        description: type.description || '',
        montant_suggere: type.montant_suggere || '',
        recurrence: type.recurrence || 'mensuelle'
      });
    } else {
      setEditingType(null);
      setNewContributionType({
        nom: '',
        description: '',
        montant_suggere: '',
        recurrence: 'mensuelle'
      });
    }
    onTypeOpen();
  };

  const handleSaveContributionType = async () => {
    try {
      const endpoint = editingType 
        ? `/contributions/types/${editingType.id}` 
        : '/contributions/types';
      const method = editingType ? 'PUT' : 'POST';

      console.log('💾 Saving contribution type:', { endpoint, method, data: newContributionType });

      await apiService.makeRequest(endpoint, {
        method,
        body: JSON.stringify({
          ...newContributionType,
          montant_suggere: newContributionType.montant_suggere 
            ? parseFloat(newContributionType.montant_suggere) 
            : null
        })
      });

      toast({
        title: editingType ? 'Type mis à jour' : 'Type de contribution ajouté',
        status: 'success',
        duration: 3000
      });
      
      setNewContributionType({ nom: '', description: '', montant_suggere: '', recurrence: 'mensuelle' });
      setEditingType(null);
      onTypeClose();
      fetchContributionTypes();
    } catch (err) {
      console.error('❌ Error saving contribution type:', err);
      toast({
        title: 'Erreur',
        description: err.message || 'Erreur lors de la sauvegarde du type',
        status: 'error',
        duration: 5000
      });
    }
  };


  const handleDeleteContributionType = async (id) => {
    try {
      await apiService.makeRequest(`/contributions/types/${id}`, {
        method: 'DELETE'
      });
      toast({
        title: 'Type supprimé',
        status: 'success',
        duration: 3000
      });
      fetchContributionTypes();
      onDeleteClose();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        status: 'error',
        duration: 5000
      });
    }
  };

  const confirmDelete = (item, type) => {
    setDeleteItem({ ...item, type });
    onDeleteOpen();
  };

  const executeDelete = () => {
    if (!deleteItem) return;

    switch (deleteItem.type) {
      case 'group':
        handleDeleteGroup(deleteItem.id);
        break;
      case 'function':
        handleDeleteFunction(deleteItem.id);
        break;
      case 'contributionType':
        handleDeleteContributionType(deleteItem.id);
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>Paramètres</Heading>

      <Tabs>
        <TabList>
          <Tab>Mon Profil</Tab>
          <Tab>Sécurité</Tab>
          {user?.is_admin && (
            <>
              <Tab>Groupes</Tab>
              <Tab>Fonctions</Tab>
              <Tab>Types de Contributions</Tab>
              <Tab>Maintenance</Tab>
              <Tab>Journal d'audit</Tab>
            </>
          )}
        </TabList>

        <TabPanels>
          {/* Onglet Profil */}
          <TabPanel>
            <Card>
              <CardHeader>
                <HStack spacing={4}>
                  <Avatar size="lg" name={`${user?.prenom} ${user?.nom}`} />
                  <Box>
                    <Heading size="md">{user?.prenom} {user?.nom}</Heading>
                    <Text color="gray.600">{user?.email}</Text>
                    {user?.is_admin && <Badge colorScheme="green">Administrateur</Badge>}
                  </Box>
                </HStack>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Nom</FormLabel>
                      <Input
                        value={profileData.nom}
                        onChange={(e) => setProfileData({ ...profileData, nom: e.target.value })}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Prénom</FormLabel>
                      <Input
                        value={profileData.prenom}
                        onChange={(e) => setProfileData({ ...profileData, prenom: e.target.value })}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Téléphone</FormLabel>
                    <Input
                      value={profileData.telephone}
                      onChange={(e) => setProfileData({ ...profileData, telephone: e.target.value })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Adresse postale</FormLabel>
                    <Input
                      value={profileData.adresse_postale}
                      onChange={(e) => setProfileData({ ...profileData, adresse_postale: e.target.value })}
                    />
                  </FormControl>

                  <Button colorScheme="blue" onClick={handleProfileUpdate}>
                    Enregistrer les modifications
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Onglet Sécurité */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">Changer le mot de passe</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Mot de passe actuel</FormLabel>
                    <Input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Nouveau mot de passe</FormLabel>
                    <Input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Confirmer le nouveau mot de passe</FormLabel>
                    <Input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    />
                  </FormControl>

                  <Button colorScheme="blue" onClick={handlePasswordChange}>
                    Changer le mot de passe
                  </Button>

                  <Divider />

                  <Button colorScheme="red" variant="outline" onClick={logout}>
                    Se déconnecter
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Onglet Groupes (Admin) */}
          {user?.is_admin && (
            <TabPanel>
              <Card mb={4}>
                <CardHeader>
                  <Heading size="md">Groupes de l'église</Heading>
                </CardHeader>
                <CardBody>
                  <Button colorScheme="green" onClick={() => openGroupModal()} mb={4}>
                    Ajouter un groupe
                  </Button>
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>Nom</Th>
                        <Th>Description</Th>
                        <Th>Membres</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {groups.length === 0 ? (
                        <Tr>
                          <Td colSpan={4} textAlign="center" py={8}>
                            <Text color="gray.500">Aucun groupe trouvé</Text>
                          </Td>
                        </Tr>
                      ) : (
                        groups.map((group) => (
                          <Tr key={group.id}>
                            <Td fontWeight="semibold">{group.nom}</Td>
                            <Td>{group.description || '-'}</Td>
                            <Td><Badge>{group.membre_count || 0}</Badge></Td>
                            <Td>
                              <HStack>
                                <IconButton
                                  icon={<FiEdit2 />}
                                  size="sm"
                                  onClick={() => openGroupModal(group)}
                                  aria-label="Modifier"
                                />
                                <IconButton
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  colorScheme="red"
                                  onClick={() => confirmDelete(group, 'group')}
                                  aria-label="Supprimer"
                                />
                              </HStack>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </CardBody>
              </Card>
            </TabPanel>
          )}

          {/* Onglet Fonctions (Admin) */}
          {user?.is_admin && (
            <TabPanel>
              <Card mb={4}>
                <CardHeader>
                  <Heading size="md">Fonctions dans l'église</Heading>
                </CardHeader>
                <CardBody>
                  <Button colorScheme="green" onClick={() => openFunctionModal()} mb={4}>
                    Ajouter une fonction
                  </Button>
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>Nom</Th>
                        <Th>Description</Th>
                        <Th>Membres</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {functions.map((func) => (
                        <Tr key={func.id}>
                          <Td fontWeight="semibold">{func.nom}</Td>
                          <Td>{func.description || '-'}</Td>
                          <Td><Badge>{func.membre_count || 0}</Badge></Td>
                          <Td>
                            <HStack>
                              <IconButton
                                icon={<FiEdit2 />}
                                size="sm"
                                onClick={() => openFunctionModal(func)}
                                aria-label="Modifier"
                              />
                              <IconButton
                                icon={<FiTrash2 />}
                                size="sm"
                                colorScheme="red"
                                onClick={() => confirmDelete(func, 'function')}
                                aria-label="Supprimer"
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </CardBody>
              </Card>
            </TabPanel>
          )}

          {/* Onglet Types de Contributions (Admin) */}
          {user?.is_admin && (
            <TabPanel>
              <Card mb={4}>
                <CardHeader>
                  <Heading size="md">Types de cotisations</Heading>
                </CardHeader>
                <CardBody>
                  <Button colorScheme="green" onClick={() => openTypeModal()} mb={4}>
                    Ajouter un type
                  </Button>
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>Nom</Th>
                        <Th>Description</Th>
                        <Th>Montant suggéré</Th>
                        <Th>Récurrence</Th>
                        <Th>Utilisations</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {contributionTypes.map((type) => (
                        <Tr key={type.id}>
                          <Td fontWeight="semibold">{type.nom}</Td>
                          <Td>{type.description || '-'}</Td>
                          <Td>{type.montant_suggere ? `${type.montant_suggere} €` : '-'}</Td>
                          <Td>{type.recurrence || '-'}</Td>
                          <Td><Badge>{type.usage_count || 0}</Badge></Td>
                          <Td>
                            <HStack>
                              <IconButton
                                icon={<FiEdit2 />}
                                size="sm"
                                onClick={() => openTypeModal(type)}
                                aria-label="Modifier"
                              />
                              <IconButton
                                icon={<FiTrash2 />}
                                size="sm"
                                colorScheme="red"
                                onClick={() => confirmDelete(type, 'contributionType')}
                                aria-label="Supprimer"
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </CardBody>
              </Card>
            </TabPanel>
          )}

          {/* Onglet Maintenance (Admin) */}
          {user?.is_admin && (
            <TabPanel>
              <Card>
                <CardHeader>
                  <Heading size="md">🔧 Maintenance des données</Heading>
                </CardHeader>
                <CardBody>
                  <GenerateCardsSection />
                </CardBody>
              </Card>
            </TabPanel>
          )}

          {/* Onglet Journal d'audit (Admin) */}
          {user?.is_admin && (
            <TabPanel>
              <Card>
                <CardHeader>
                  <Heading size="md">📋 Journal d'audit</Heading>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Historique de toutes les modifications effectuées par les administrateurs.
                  </Text>
                </CardHeader>
                <CardBody>
                  <AuditLogSection />
                </CardBody>
              </Card>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* Modal Groupe */}
      <Modal isOpen={isGroupOpen} onClose={onGroupClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingGroup ? 'Modifier le groupe' : 'Nouveau groupe'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nom</FormLabel>
                <Input
                  value={newGroup.nom}
                  onChange={(e) => setNewGroup({ ...newGroup, nom: e.target.value })}
                  placeholder="Ex: Union des femmes chrétiennes"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={newGroup.description}
                  onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                  placeholder="Description du groupe"
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onGroupClose}>
              Annuler
            </Button>
            <Button colorScheme="green" onClick={handleSaveGroup}>
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal Fonction */}
      <Modal isOpen={isFunctionOpen} onClose={onFunctionClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingFunction ? 'Modifier la fonction' : 'Nouvelle fonction'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nom</FormLabel>
                <Input
                  value={newFunction.nom}
                  onChange={(e) => setNewFunction({ ...newFunction, nom: e.target.value })}
                  placeholder="Ex: Président de l'association"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={newFunction.description}
                  onChange={(e) => setNewFunction({ ...newFunction, description: e.target.value })}
                  placeholder="Description de la fonction"
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFunctionClose}>
              Annuler
            </Button>
            <Button colorScheme="green" onClick={handleSaveFunction}>
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal Type de cotisation */}
      <Modal isOpen={isTypeOpen} onClose={onTypeClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingType ? 'Modifier le type' : 'Nouveau type de cotisation'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nom</FormLabel>
                <Input
                  value={newContributionType.nom}
                  onChange={(e) => setNewContributionType({ ...newContributionType, nom: e.target.value })}
                  placeholder="Ex: Dîme mensuelle"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={newContributionType.description}
                  onChange={(e) => setNewContributionType({ ...newContributionType, description: e.target.value })}
                  placeholder="Description du type de cotisation"
                  rows={3}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Montant suggéré (€)</FormLabel>
                <Input
                  type="number"
                  value={newContributionType.montant_suggere}
                  onChange={(e) => setNewContributionType({ ...newContributionType, montant_suggere: e.target.value })}
                  placeholder="Ex: 50"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Récurrence</FormLabel>
                <Select
                  value={newContributionType.recurrence}
                  onChange={(e) => setNewContributionType({ ...newContributionType, recurrence: e.target.value })}
                >
                  <option value="mensuelle">Mensuelle</option>
                  <option value="annuelle">Annuelle</option>
                  <option value="ponctuelle">Ponctuelle</option>
                  <option value="hebdomadaire">Hebdomadaire</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTypeClose}>
              Annuler
            </Button>
            <Button colorScheme="green" onClick={handleSaveContributionType}>
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* AlertDialog pour confirmation de suppression */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmer la suppression
            </AlertDialogHeader>

            <AlertDialogBody>
              Êtes-vous sûr de vouloir supprimer "{deleteItem?.nom}" ? 
              Cette action est irréversible.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Annuler
              </Button>
              <Button colorScheme="red" onClick={executeDelete} ml={3}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}